import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/apiClient";
import { startGoogleLogin } from "../lib/googleAuth";
import { toast } from "sonner";
import { FooterLinks } from "../components/Footer";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ artist_name: "", email: "", password: "", confirm: "" });
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginGoogle = () => { startGoogleLogin(); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) return setError("Les mots de passe ne correspondent pas.");
    if (form.password.length < 8) return setError("Le mot de passe doit contenir au moins 8 caractères.");
    if (!/[A-Za-z]/.test(form.password) || !/\d/.test(form.password)) return setError("Le mot de passe doit contenir au moins une lettre et un chiffre.");
    if (!accept) return setError("Tu dois accepter les conditions.");
    setLoading(true);
    try {
      const res = await register(form.artist_name, form.email, form.password);
      if (res?.needs_verification) {
        toast.success("Code de vérification envoyé par email");
        navigate("/verify-email", { state: { email: form.email } });
      } else {
        navigate("/onboarding");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-amber-600/10 blur-3xl" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-amber-800/10 blur-3xl" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative z-10">
        <div className="flex items-center gap-2 justify-center mb-8">
          <img src={process.env.PUBLIC_URL + "/cehnura-mark.png"} alt="Cehnura" className="w-11 h-11 object-contain" />
          <span className="font-display font-extrabold text-2xl">Cehn<span className="gold-text">ura</span></span>
        </div>
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-8 relative grain">
          <div className="relative z-10">
            <h1 className="font-display text-2xl font-bold mb-1">Lance ta carrière</h1>
            <p className="text-sm text-zinc-500 mb-6">Ton assistant de carrière t'attend.</p>
            <form onSubmit={submit} className="space-y-4">
              <Field label="Nom d'artiste" value={form.artist_name} onChange={set("artist_name")} testid="register-artist-name" placeholder="Ton nom de scène" />
              <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="register-email" placeholder="ton@email.com" />
              <Field label="Mot de passe" type="password" value={form.password} onChange={set("password")} testid="register-password" placeholder="••••••••" />
              <Field label="Confirmer le mot de passe" type="password" value={form.confirm} onChange={set("confirm")} testid="register-confirm" placeholder="••••••••" />
              <label className="flex items-start gap-2 text-sm text-zinc-400 cursor-pointer">
                <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} data-testid="register-accept" className="mt-1 accent-amber-500 w-4 h-4" />
                <span>J'accepte les <a href="/legal/cgu" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline">conditions d'utilisation</a> et la <a href="/legal/confidentialite" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline">politique de confidentialité</a>.</span>
              </label>
              {error && <p className="text-sm text-red-400" data-testid="register-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="register-submit" className="w-full h-12 rounded-xl gold-gradient text-black font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Créer mon compte"}
              </button>
            </form>
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-zinc-800" /><span className="text-xs text-zinc-600">ou</span><div className="flex-1 h-px bg-zinc-800" />
            </div>
            <button type="button" onClick={loginGoogle} data-testid="google-register-btn"
              className="w-full h-12 rounded-xl bg-white text-zinc-900 font-semibold flex items-center justify-center gap-2 hover:bg-zinc-100 transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
              Continuer avec Google
            </button>
            <p className="text-sm text-zinc-500 mt-6 text-center">
              Déjà un compte ?{" "}
              <Link to="/login" className="text-amber-400 hover:underline" data-testid="go-login">Connecte-toi</Link>
            </p>
          </div>
        </div>
        <FooterLinks />
      </motion.div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, placeholder }) {
  const [show, setShow] = useState(false);
  const isPwd = type === "password";
  const inputType = isPwd ? (show ? "text" : "password") : type;
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-400 mb-1.5">{label}</label>
      <div className="relative">
        <input type={inputType} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} required
          className={`w-full h-12 px-4 ${isPwd ? "pr-11" : ""} rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none transition-colors`} />
        {isPwd && (
          <button type="button" onClick={() => setShow((s) => !s)} data-testid={`${testid}-toggle`} aria-label={show ? "Masquer" : "Afficher"}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-amber-400">
            {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        )}
      </div>
    </div>
  );
}
